import React from 'react';
import {
  Select,
  FormControl,
  FormLabel,
  FormControlLabel,
  Checkbox,
  TextField,
  Radio,
  RadioGroup,
  FormHelperText,
  Grid,
  IconButton,
  MenuItem
} from '@mui/material';
import useStyles from '../useStyles';

import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ErrorIcon from '@mui/icons-material/Error';
import AddressInput from '../../../NewUI/Components/CustomUIElements/AddressInput';
import { stepPropTypes } from './types';

function Step1({ answers, setAnswers, jobAdParams, requiredRefs, activeError }) {
  const [value, setValue] = React.useState({
    description: [answers.address, answers.city, answers.state].filter((cur) => cur).join(', ')
  });

  const classes = useStyles();

  return (
    <div className={classes.stepContainer}>
      <h4>Let&apos;s get started</h4>

      <FormControl
        ref={requiredRefs.companyName}
        fullWidth={true}
        className={`${classes.formControl} ${classes.medium}`}
      >
        <FormLabel required={true} className={classes.label}>
          Company name for this job
        </FormLabel>
        <TextField
          error={activeError === requiredRefs.companyName}
          className={classes.textField}
          variant="outlined"
          value={answers.companyName}
          onChange={(e) =>
            e.target.value.length <= 52 && setAnswers({ ...answers, companyName: e.target.value })
          }
          InputProps={{
            endAdornment: activeError === requiredRefs.companyName && (
              <div className={classes.error}>
                <ErrorIcon color="error" />
                <span>This field is required</span>
              </div>
            )
          }}
        />
        <FormHelperText className={classes.helperText}>
          {answers.companyName.length}/52
        </FormHelperText>
      </FormControl>

      <FormControl
        ref={requiredRefs.industry}
        fullWidth={true}
        className={`${classes.formControl} ${classes.medium}`}
      >
        <FormLabel required={true} className={classes.label}>
          Industry
        </FormLabel>
        <Select
          classes={{ select: classes.input }}
          variant="outlined"
          value={answers.industry}
          onChange={(e) => setAnswers({ ...answers, industry: e.target.value, subIndustry: '' })}
          displayEmpty={true}
          renderValue={(value) =>
            (value && value[0]) || <span className={classes.placeholder}>Select an option</span>
          }
          endAdornment={
            activeError === requiredRefs.industry && (
              <div className={classes.error}>
                <ErrorIcon color="error" />
                <span>This field is required</span>
              </div>
            )
          }
        >
          {jobAdParams.industry_options.map((industry) => (
            <MenuItem key={industry[1]} value={industry}>
              {industry[0]}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth={true} className={`${classes.formControl} ${classes.medium}`}>
        <FormLabel className={classes.label}>Sub-industry</FormLabel>
        <Select
          disabled={!answers.industry}
          classes={{ select: classes.input }}
          variant="outlined"
          value={answers.subIndustry}
          onChange={(e) => setAnswers({ ...answers, subIndustry: e.target.value })}
          displayEmpty={true}
          renderValue={(value) =>
            value ? value[0] : <span className={classes.placeholder}>Select an option</span>
          }
        >
          {answers.industry &&
            jobAdParams.sub_industry_options[answers.industry[1]].map((subIndustry) => (
              <MenuItem key={subIndustry[1]} value={subIndustry}>
                {subIndustry[0]}
              </MenuItem>
            ))}
        </Select>
      </FormControl>

      <FormControl
        ref={requiredRefs.jobTitle}
        fullWidth={true}
        className={`${classes.formControl} ${classes.medium}`}
      >
        <FormLabel required={true} className={classes.label}>
          Job title
        </FormLabel>
        <TextField
          error={activeError === requiredRefs.jobTitle}
          className={classes.textField}
          variant="outlined"
          value={answers.jobTitle}
          onChange={(e) =>
            e.target.value.length <= 52 && setAnswers({ ...answers, jobTitle: e.target.value })
          }
          InputProps={{
            endAdornment: activeError === requiredRefs.jobTitle && (
              <div className={classes.error}>
                <ErrorIcon color="error" />
                <span>This field is required</span>
              </div>
            )
          }}
        />
        <FormHelperText className={classes.helperText}>
          {answers.companyName.length}/52
        </FormHelperText>
      </FormControl>

      <FormControl fullWidth={true} className={`${classes.formControl} ${classes.medium}`}>
        <FormLabel focused={false} className={classes.label}>
          Where will an employee report to work?
        </FormLabel>
        <RadioGroup
          value={answers.reportToWork}
          onChange={(e) => setAnswers({ ...answers, reportToWork: e.target.value })}
        >
          <FormControlLabel
            label="Employees will report to a specific address"
            value={'true'}
            control={<Radio />}
          />
          <FormControlLabel
            label="Employees will not report to a specific address"
            value={'false'}
            control={<Radio />}
          />
        </RadioGroup>
      </FormControl>

      <AddressInput
        requiredRef={requiredRefs.address}
        classes={classes}
        value={value}
        setValue={setValue}
        activeError={activeError === requiredRefs.address}
        answers={answers}
        setAnswers={setAnswers}
      />

      <div className={`${classes.formControl} ${classes.medium} ${classes.greyBox}`}>
        <FormControl fullWidth={true}>
          <Grid container alignItems="center">
            <span>Country:</span>
            <Select
              classes={{
                select: classes.greyInput,
                icon: classes.greyIcon
              }}
              value={answers.country}
              renderValue={(value) =>
                jobAdParams.country_options.find((cur) => cur[1] === value)[0]
              }
              onChange={(e) => {
                setValue(null);
                setAnswers({
                  ...answers,
                  country: e.target.value,
                  address: '',
                  city: '',
                  state: ''
                });
              }}
              IconComponent={EditIcon}
              disableUnderline
            >
              {jobAdParams.country_options.map((country) => (
                <MenuItem key={country[1]} value={country[1]}>
                  {country[0]}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        </FormControl>
        <FormControl>
          <Grid container alignItems="center">
            <span>Job posting will be displayed in:</span>
            <Select
              classes={{
                select: classes.greyInput,
                icon: classes.greyIcon
              }}
              value={answers.language}
              renderValue={(value) =>
                jobAdParams.languages_options.find((cur) => cur[1] === value)[0]
              }
              onChange={(e) => setAnswers({ ...answers, language: e.target.value })}
              IconComponent={EditIcon}
              disableUnderline
            >
              {jobAdParams.languages_options.map((language) => (
                <MenuItem key={language[1]} value={language[1]}>
                  {language[0]}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        </FormControl>
      </div>

      <FormControl fullWidth={true} className={`${classes.formControl}`}>
        <FormLabel focused={false} className={classes.label}>
          Can this job be performed remotely, meaning primarily from home? If so, we’ll add a
          “Remote” tag to your post
        </FormLabel>
        <RadioGroup
          value={answers.remoteWork}
          onChange={(e) => setAnswers({ ...answers, remoteWork: e.target.value })}
        >
          {jobAdParams.remote_type_options.map((remote) => (
            <FormControlLabel
              key={remote[1]}
              label={remote[0]}
              value={remote[1]}
              control={<Radio />}
            />
          ))}
        </RadioGroup>
      </FormControl>

      <FormControl
        ref={requiredRefs.hires}
        fullWidth={true}
        className={`${classes.formControl} ${classes.medium}`}
      >
        <FormLabel focused={false} required={true} className={classes.label}>
          How many hires?
        </FormLabel>
        <span className={classes.subLabel}>
          This helps us match you with the right number of applicants.
        </span>
        <Grid
          container
          classes={{
            root: `${classes.controlledNumberInput} ${activeError === requiredRefs.hires ? classes.errorOutline : ''}`
          }}
        >
          <TextField
            type="number"
            variant="outlined"
            className={`${classes.textField}`}
            value={answers.hires}
            onChange={(e) => setAnswers({ ...answers, hires: parseInt(e.target.value) || 0 })}
            InputProps={{
              classes: { root: classes.w200 },
              endAdornment: activeError === requiredRefs.hires && (
                <div className={classes.error}>
                  <ErrorIcon color="error" />
                  <span>This field is required</span>
                </div>
              )
            }}
          />
          <IconButton
            onClick={() => setAnswers({ ...answers, hires: answers.hires - 1 })}
            disabled={answers.hires <= 0 || answers.hiring}
          >
            <RemoveIcon />
          </IconButton>
          <IconButton
            onClick={() => setAnswers({ ...answers, hires: answers.hires + 1 })}
            disabled={answers.hires >= 100 || answers.hiring}
          >
            <AddIcon />
          </IconButton>
        </Grid>
        <Grid container style={{ marginTop: 10 }}>
          <FormControlLabel
            label="Always Hiring"
            control={
              <Checkbox
                checked={answers.hiring}
                onChange={() => setAnswers({ ...answers, hiring: !answers.hiring })}
              />
            }
          />
        </Grid>
      </FormControl>
    </div>
  );
}

Step1.propTypes = stepPropTypes;

export default Step1;
